import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import clsx from "clsx";
import {
  CallApiSendEmailOtp,
  CallApiUpdateInvoiceInfo,
} from "../../../Api/Enforcement";
import { ShowCustomErrorModal } from "../../Atoms/CustomErrorModal";
import { SwalSuccess } from "../../../Share/Swal";
import useReCaptchaV3 from "../../../Hooks/useReCaptChaV3";
import Styles from "./index.module.scss";

const RECAPTCHA_REQUIRED = "Vui lòng hoàn thành xác minh reCAPTCHA.";
const RECAPTCHA_INVALID = "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const IDENTITY_PATTERN = /^(\d{9}|\d{12})$/;

const textValue = (value) => (value == null ? "" : String(value).trim());

const identityValue = (value) => {
  const text = textValue(value);
  return text === "0" ? "" : text;
};

const buildInvoiceForm = (user) => ({
  AccountName: textValue(user?.AccountName),
  Email: textValue(user?.Email),
  OTP: "",
  IdentityNumber: identityValue(user?.IdentityNumber),
  Address: textValue(user?.Address),
});

const getApiErrorMessage = (res) => {
  const status = res?.Status;
  if (status) {
    if (status.isNetworkError) {
      return "Thực hiện chưa thành công. Vui lòng thử lại hoặc liên hệ fanpage.";
    }

    if (status.status === 403) return RECAPTCHA_INVALID;

    const data = status.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data?.Message) return data.Message;
    return "Thử tải lại trang hoặc liên hệ fanpage để được hỗ trợ";
  }

  if (res?.success === false || res?.Success === false) {
    return res?.Message || res?.message || "Đã xảy ra lỗi";
  }

  return "";
};

const validateInvoiceField = (name, value) => {
  const text = textValue(value);

  if (name === "AccountName" && !text) return "AccountName is require";
  if (name === "OTP" && !text) return "OTP is require";

  if (name === "Email") {
    if (!text) return "Email is require";
    if (!EMAIL_PATTERN.test(text)) return "Email không đúng định dạng";
    if (text.length > 128) return "Email tối đa 128 ký tự";
  }

  if (name === "IdentityNumber") {
    if (!text) return "IdentityNumber is require";
    if (!IDENTITY_PATTERN.test(text)) {
      return "IdentityNumber phải gồm 9 số (CMND) hoặc 12 số (CCCD)";
    }
  }

  if (name === "Address") {
    if (!text) return "Address is require";
    if (text.length > 254) return "Address length must be less than 255 characters";
  }

  return "";
};

const validateInvoiceForm = (form) => {
  const nextErrors = {};
  Object.keys(form).forEach((name) => {
    const message = validateInvoiceField(name, form[name]);
    if (message) nextErrors[name] = message;
  });
  return nextErrors;
};

function useInvoiceRecaptcha(active) {
  const reCaptchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [isReCaptchaReady, executeReCaptcha] = useReCaptchaV3();
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState("");
  const isDefaultV3 = process.env.REACT_APP_RECAPTCHA_DEFAULT === "3";
  const version = searchParams.get("version");
  const useV2 = version === "2" || !isDefaultV3;

  useEffect(() => {
    if (!active || !useV2) return;

    let cancelled = false;
    let intervalId = null;

    const renderWidget = () => {
      if (cancelled || !reCaptchaRef.current || !window.grecaptcha?.render) return;
      if (widgetIdRef.current != null || reCaptchaRef.current.childElementCount > 0) {
        setRecaptchaLoading(false);
        return;
      }

      try {
        widgetIdRef.current = window.grecaptcha.render(reCaptchaRef.current, {
          sitekey: process.env.REACT_APP_RECAPTCHA_KEY_V2,
          callback: () => setRecaptchaError(""),
          "expired-callback": () =>
            setRecaptchaError("Mã xác thực reCAPTCHA đã hết hạn.\nVui lòng xác minh lại."),
          "error-callback": () =>
            setRecaptchaError("Lỗi tải reCAPTCHA.\nVui lòng thử lại hoặc tải lại trang."),
        });
        setRecaptchaLoading(false);
      } catch (error) {
        if (!String(error?.message || "").includes("already been rendered")) {
          setRecaptchaError("Không thể tải reCAPTCHA.\nVui lòng tải lại trang.");
          setRecaptchaLoading(false);
        }
      }
    };

    const waitForRecaptcha = () => {
      intervalId = window.setInterval(() => {
        if (window.grecaptcha?.render) {
          window.clearInterval(intervalId);
          renderWidget();
        }
      }, 100);

      window.setTimeout(() => {
        if (intervalId) window.clearInterval(intervalId);
        if (!cancelled && !window.grecaptcha?.render) {
          setRecaptchaLoading(false);
          setRecaptchaError("Không thể tải reCAPTCHA.\nVui lòng tải lại trang.");
        }
      }, 5000);
    };

    if (window.grecaptcha?.render) {
      renderWidget();
    } else {
      setRecaptchaLoading(true);
      const existingScript = document.querySelector(
        'script[src*="recaptcha/api.js"]'
      );
      if (!existingScript) {
        const script = document.createElement("script");
        script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.onload = waitForRecaptcha;
        script.onerror = () => {
          if (cancelled) return;
          setRecaptchaLoading(false);
          setRecaptchaError("Không thể tải reCAPTCHA.\nVui lòng tải lại trang.");
        };
        document.head.appendChild(script);
      } else {
        waitForRecaptcha();
      }
    }

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [active, useV2]);

  const resetRecaptcha = () => {
    if (!useV2 || widgetIdRef.current == null) return;
    try {
      window.grecaptcha?.reset(widgetIdRef.current);
    } catch (error) {
      // Widget có thể đã bị gỡ khi đóng modal.
    }
  };

  const getRecaptchaToken = async (action) => {
    const token = { token_v2: "", token_v3: "" };

    if (useV2) {
      const v2Token = window.grecaptcha?.getResponse(widgetIdRef.current);
      if (!v2Token) throw new Error(RECAPTCHA_REQUIRED);
      token.token_v2 = v2Token;
      return token;
    }

    if (!isReCaptchaReady) throw new Error(RECAPTCHA_INVALID);
    token.token_v3 = await executeReCaptcha(action);
    if (!token.token_v3) throw new Error(RECAPTCHA_INVALID);
    return token;
  };

  return {
    useV2,
    reCaptchaRef,
    recaptchaLoading,
    recaptchaError,
    getRecaptchaToken,
    resetRecaptcha,
  };
}

function UpdateInvoiceModal({ show, user, onClose, onUpdated }) {
  const [form, setForm] = useState(() => buildInvoiceForm(user));
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    useV2,
    reCaptchaRef,
    recaptchaLoading,
    recaptchaError,
    getRecaptchaToken,
    resetRecaptcha,
  } = useInvoiceRecaptcha(show);

  useEffect(() => {
    if (!show) return;
    setForm(buildInvoiceForm(user));
    setFieldErrors({});
  }, [show, user]);

  const handleChange = (name, value) => {
    const nextValue =
      name === "IdentityNumber" ? value.replace(/\D/g, "").slice(0, 12) : value;
    setForm((current) => ({ ...current, [name]: nextValue }));
    setFieldErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleSendOtp = async () => {
    if (isSendingOtp) return;

    const emailError = validateInvoiceField("Email", form.Email);
    if (emailError) {
      setFieldErrors((current) => ({ ...current, Email: emailError }));
      return;
    }

    let token;
    try {
      token = await getRecaptchaToken("sendEmailOtp");
    } catch (error) {
      ShowCustomErrorModal({
        title: "Đã xảy ra lỗi",
        message: `<p>${error.message || RECAPTCHA_INVALID}</p>`,
      });
      return;
    }

    setIsSendingOtp(true);
    CallApiSendEmailOtp(
      token,
      {
        AccountName: textValue(form.AccountName),
        Email: textValue(form.Email),
      },
      (res) => {
        setIsSendingOtp(false);
        const message = getApiErrorMessage(res);
        if (message) {
          ShowCustomErrorModal({
            title: "Đã xảy ra lỗi",
            message: `<p>${message}</p>`,
          });
          return;
        }

        SwalSuccess("Mã OTP đã được gửi tới email của bạn");
      }
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const nextErrors = validateInvoiceForm(form);
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    let token;
    try {
      token = await getRecaptchaToken("updateInvoice");
    } catch (error) {
      ShowCustomErrorModal({
        title: "Đã xảy ra lỗi",
        message: `<p>${error.message || RECAPTCHA_INVALID}</p>`,
      });
      return;
    }

    const payload = {
      AccountName: textValue(form.AccountName),
      OTP: textValue(form.OTP),
      Email: textValue(form.Email),
      IdentityNumber: textValue(form.IdentityNumber),
      Address: textValue(form.Address),
    };

    setIsSubmitting(true);
    CallApiUpdateInvoiceInfo(token, payload, (res) => {
      setIsSubmitting(false);
      resetRecaptcha();
      const message = getApiErrorMessage(res);
      if (message) {
        ShowCustomErrorModal({
          title: "Đã xảy ra lỗi",
          message: `<p>${message}</p>`,
        });
        return;
      }

      onUpdated?.({
        Email: payload.Email,
        IdentityNumber: payload.IdentityNumber,
        Address: payload.Address,
      });
      SwalSuccess("Cập nhật thông tin hóa đơn thành công");
      onClose();
    });
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      style={{ zIndex: 250000 }}
      backdropClassName={clsx(Styles.backdrop)}
      dialogClassName={clsx(Styles.dialog)}
      contentClassName={clsx(Styles.content)}
    >
      <Modal.Header closeButton className={clsx(Styles.header)}>
        <Modal.Title>Cập nhật thông tin hóa đơn</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body className={clsx(Styles.body)}>
          <div className={clsx(Styles.form)}>
            <div>
              <label htmlFor="invoice-account">Tài khoản</label>
              <input
                id="invoice-account"
                value={form.AccountName}
                readOnly
                className={clsx(Styles.readOnlyInput)}
              />
              {fieldErrors.AccountName && (
                <span className={clsx(Styles.fieldError)}>
                  {fieldErrors.AccountName}
                </span>
              )}
            </div>
            <div>
              <label htmlFor="invoice-email">Email</label>
              <div className={clsx(Styles.emailRow)}>
                <input
                  id="invoice-email"
                  type="email"
                  maxLength={128}
                  placeholder="Nhập email"
                  value={form.Email}
                  onChange={(event) => handleChange("Email", event.target.value)}
                />
                <button
                  type="button"
                  className={clsx(Styles.getOtpButton)}
                  onClick={handleSendOtp}
                  disabled={isSendingOtp}
                >
                  {isSendingOtp ? "Đang gửi..." : "Lấy OTP"}
                </button>
              </div>
              {fieldErrors.Email && (
                <span className={clsx(Styles.fieldError)}>{fieldErrors.Email}</span>
              )}
            </div>
            <div>
              <label htmlFor="invoice-otp">OTP</label>
              <input
                id="invoice-otp"
                placeholder="Nhập mã OTP"
                value={form.OTP}
                onChange={(event) => handleChange("OTP", event.target.value)}
              />
              {fieldErrors.OTP && (
                <span className={clsx(Styles.fieldError)}>{fieldErrors.OTP}</span>
              )}
            </div>
            <div>
              <label htmlFor="invoice-identity">CCCD/CMND</label>
              <input
                id="invoice-identity"
                inputMode="numeric"
                maxLength={12}
                placeholder="9 số (CMND) hoặc 12 số (CCCD)"
                value={form.IdentityNumber}
                onChange={(event) =>
                  handleChange("IdentityNumber", event.target.value)
                }
              />
              {fieldErrors.IdentityNumber && (
                <span className={clsx(Styles.fieldError)}>
                  {fieldErrors.IdentityNumber}
                </span>
              )}
            </div>
            <div>
              <label htmlFor="invoice-address">Địa chỉ</label>
              <textarea
                id="invoice-address"
                maxLength={254}
                placeholder="Nhập địa chỉ"
                value={form.Address}
                onChange={(event) => handleChange("Address", event.target.value)}
              />
              {fieldErrors.Address && (
                <span className={clsx(Styles.fieldError)}>{fieldErrors.Address}</span>
              )}
            </div>
            {useV2 && (
              <div className={clsx(Styles.recaptcha)}>
                <div id="invoice-recaptcha" ref={reCaptchaRef}></div>
                {recaptchaLoading && <p>Đang tải reCAPTCHA...</p>}
                {recaptchaError && <p>{recaptchaError}</p>}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className={clsx(Styles.footer)}>
          <button
            type="submit"
            className={clsx(Styles.submitButton)}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Đang cập nhật..." : "Cập nhật"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default UpdateInvoiceModal;
